import { Component, OnInit, ViewChild } from '@angular/core';
import { first } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AlertService } from '@app/_services';
import { AppEventService, AppEvent } from './app-event.service';
import { AppEventDetailComponent } from './app-event-detail.component';
import { environment } from '@environments/environment';

@Component({
	selector: 'app-event-list',
	templateUrl: './app-event-list.component.html',
	styleUrls: ['./app-event-list.component.css'],
	providers: [MessageService, ConfirmationService],
	standalone: false
})
export class AppEventListComponent implements OnInit {

	@ViewChild('dt') table: Table;

	constructor(
		private appEventService: AppEventService,
		private alertService: AlertService,
		private messageService: MessageService,
		private dialog: MatDialog
	) {}

	// Filter state
	dateFrom: string = '';
	dateTo: string = '';
	eventType: string = '';
	eventCategory: string = '';
	deviceType: string = '';
	userIdFilter: string = '';
	visitorIdFilter: string = '';
	propertyIdFilter: string = '';
	searchQueryFilter: string = '';
	globalFilter: string = '';
	autoRefresh = false;
	refreshHandle: any = null;

	// Dropdown data
	eventTypes: string[] = [];
	eventCategories: string[] = [];
	deviceTypes: string[] = [];

	// List data
	events: AppEvent[] = [];
	recordTotal = 0;
	loading = true;
	first = 0;
	rows = 50;
	lazyLoadEvent: LazyLoadEvent;
	multiSortMeta: any[] = [];

	// Summary + chart
	summary: { total: number; breakdown: { [k: string]: number }; byCategory: { [k: string]: number } } = {
		total: 0, breakdown: {}, byCategory: {}
	};
	summaryLoading = true;
	topUsers: { userId: number; username: string; count: number }[] = [];
	chartData: any;
	chartOptions: any;

	// Columns
	columns = [
		{ field: 'appEventId', header: 'Id', width: '80px' },
		{ field: 'clickedAt', header: 'When', width: '160px' },
		{ field: 'eventType', header: 'Event Type', width: '180px' },
		{ field: 'eventCategory', header: 'Category', width: '120px' },
		{ field: 'username', header: 'User', width: '160px' },
		{ field: 'deviceType', header: 'Device', width: '100px' },
		{ field: 'propertyId', header: 'Property', width: '90px' },
		{ field: 'searchQuery', header: 'Query', width: 'auto' },
		{ field: 'responseTimeMs', header: 'Resp (ms)', width: '90px' },
		{ field: 'metadata', header: 'Metadata', width: '260px' },
	];

	categoryColors: { [k: string]: string } = {
		TRACKING: '#1976d2',
		AI: '#7b1fa2',
		SEARCH: '#00796b',
		BOOKING: '#2e7d32',
		ENGAGEMENT: '#f57c00',
		NAVIGATION: '#455a64',
		AUTH: '#c62828',
		OTHER: '#9e9e9e'
	};

	ngOnInit(): void {
		// Default to last 7 days
		const today = new Date();
		const weekAgo = new Date();
		weekAgo.setDate(today.getDate() - 7);
		this.dateTo = today.toISOString().substring(0, 10);
		this.dateFrom = weekAgo.toISOString().substring(0, 10);

		this.multiSortMeta = [{ field: 'clickedAt', order: -1 }];

		this.appEventService.getMetadata().pipe(first()).subscribe((res: any) => {
			this.eventTypes = res.data?.metadata?.eventTypes || [];
			this.eventCategories = res.data?.metadata?.eventCategories || [];
			this.deviceTypes = res.data?.metadata?.deviceTypes || [];
		});

		this.loadDashboard();
	}

	ngOnDestroy(): void {
		if (this.refreshHandle) clearInterval(this.refreshHandle);
	}

	// ─── Dashboard ─────────────────────────────────────────────────────────

	loadDashboard() {
		this.summaryLoading = true;

		forkJoin({
			summary: this.appEventService.getSummary(this.dateFrom, this.dateTo),
			series: this.appEventService.getTimeseries(this.dateFrom, this.dateTo, this.eventType || undefined, this.eventCategory || undefined),
			topUsers: this.appEventService.getTopUsers(this.dateFrom, this.dateTo, this.eventType || undefined, 10)
		}).pipe(first()).subscribe(
			(res: any) => {
				this.summary = {
					total: res.summary.data?.total || 0,
					breakdown: res.summary.data?.breakdown || {},
					byCategory: res.summary.data?.byCategory || {}
				};
				this.topUsers = res.topUsers.data?.topUsers || [];
				this.buildChart(res.series.data?.series || []);
			},
			(err: any) => { console.log('Dashboard error', err); this.alertService.error(err); },
			() => { this.summaryLoading = false; }
		);
	}

	buildChart(series: { date: string; count: number }[]) {
		this.chartData = {
			labels: series.map(p => p.date),
			datasets: [{
				label: 'Events per day',
				data: series.map(p => p.count),
				fill: true,
				borderColor: '#1976d2',
				backgroundColor: 'rgba(25, 118, 210, 0.1)',
				tension: 0.3
			}]
		};
		this.chartOptions = {
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: { display: false } },
			scales: { y: { beginAtZero: true } }
		};
	}

	// ─── Table ─────────────────────────────────────────────────────────────

	loadLazy(event: LazyLoadEvent) {
		this.loading = true;
		this.lazyLoadEvent = event;
		this.first = event.first || 0;
		if (event.rows && event.rows > 0) this.rows = event.rows;

		if ((!event.filters || Object.keys(event.filters).length === 0) && !event.multiSortMeta) {
			event.multiSortMeta = [{ field: 'clickedAt', order: -1 }];
		}

		const pageNumber = Math.floor(this.first / this.rows);
		this.appEventService.findByFilter(
			pageNumber, this.rows, event.sortField, event.sortOrder,
			event.multiSortMeta, event.globalFilter, event.filters,
			this.extraFilterParams()
		).pipe(first()).subscribe(
			(res: any) => {
				this.events = res.data?.events || [];
				this.recordTotal = res.page?.recordTotal || 0;
			},
			(err: any) => { console.log('App events error', err); this.alertService.error(err); },
			() => { this.loading = false; }
		);
	}

	extraFilterParams(): { [k: string]: string | string[] } {
		const params: { [k: string]: string | string[] } = {};
		if (this.eventType) params['eventType'] = this.eventType;
		if (this.eventCategory) params['eventCategory'] = this.eventCategory;
		if (this.deviceType) params['deviceType'] = this.deviceType;
		if (this.userIdFilter) params['userId'] = this.userIdFilter;
		if (this.visitorIdFilter) params['visitorId'] = this.visitorIdFilter;
		if (this.propertyIdFilter) params['propertyId'] = this.propertyIdFilter;
		if (this.searchQueryFilter) params['searchQuery'] = this.searchQueryFilter;
		if (this.dateFrom) params['clickedAt'] = [this.dateFrom + 'T00:00:00', (this.dateTo || this.dateFrom) + 'T23:59:59'];
		return params;
	}

	applyFilters() {
		this.first = 0;
		if (this.table) this.table.first = 0;
		this.loadDashboard();
		this.loadLazy({
			first: 0, rows: this.rows, sortField: 'clickedAt', sortOrder: -1,
			multiSortMeta: this.multiSortMeta, filters: {}, globalFilter: this.globalFilter
		} as LazyLoadEvent);
	}

	clearFilters() {
		this.eventType = '';
		this.eventCategory = '';
		this.deviceType = '';
		this.userIdFilter = '';
		this.visitorIdFilter = '';
		this.propertyIdFilter = '';
		this.searchQueryFilter = '';
		this.globalFilter = '';
		this.applyFilters();
	}

	quickRange(days: number) {
		const today = new Date();
		const from = new Date();
		from.setDate(today.getDate() - days);
		this.dateFrom = from.toISOString().substring(0, 10);
		this.dateTo = today.toISOString().substring(0, 10);
		this.applyFilters();
	}

	quickCategory(cat: string) {
		this.eventCategory = cat;
		this.eventType = '';
		this.applyFilters();
	}

	quickType(type: string) {
		this.eventType = type;
		this.eventCategory = '';
		this.applyFilters();
	}

	toggleAutoRefresh() {
		this.autoRefresh = !this.autoRefresh;
		if (this.autoRefresh) {
			this.refreshHandle = setInterval(() => this.applyFilters(), 30000);
		} else if (this.refreshHandle) {
			clearInterval(this.refreshHandle);
			this.refreshHandle = null;
		}
	}

	exportCsv() {
		const params = { ...this.extraFilterParams(), sort: 'clickedAt,desc', size: '10000' };
		const url = this.appEventService.exportCsvUrl(params);
		window.open(url, '_blank');
	}

	onRowSelect(event: any) {
		const config = new MatDialogConfig();
		config.width = '700px';
		config.maxHeight = '90vh';
		config.data = { event: event.data };
		this.dialog.open(AppEventDetailComponent, config);
	}

	// ─── Helpers for template ──────────────────────────────────────────────

	categoryColor(cat: string): string { return this.categoryColors[cat] || '#9e9e9e'; }

	categoryList(): { name: string; count: number; color: string }[] {
		return Object.keys(this.summary.byCategory || {}).map(k => ({
			name: k, count: this.summary.byCategory[k], color: this.categoryColor(k)
		})).sort((a, b) => b.count - a.count);
	}

	typeList(): { name: string; count: number }[] {
		return Object.keys(this.summary.breakdown || {}).map(k => ({
			name: k, count: this.summary.breakdown[k]
		})).sort((a, b) => b.count - a.count).slice(0, 15);
	}

	formatMetadata(raw: string): string {
		if (!raw) return '';
		if (raw.length > 80) return raw.substring(0, 80) + '…';
		return raw;
	}

	eventTypeBadgeClass(type: string): string {
		if (!type) return 'event-badge';
		if (type.startsWith('2FA') || type.startsWith('LOGIN')) return 'event-badge badge-auth';
		if (type.startsWith('BOOK')) return 'event-badge badge-booking';
		if (type === 'AI_QUERY') return 'event-badge badge-ai';
		if (type === 'FAST_SEARCH') return 'event-badge badge-search';
		if (type === 'LINK_CLICK') return 'event-badge badge-tracking';
		if (type === 'PAGE_VIEW') return 'event-badge badge-nav';
		return 'event-badge';
	}
}
